import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { RefreshCw, Download, ChevronDown, ChevronUp } from 'lucide-react';

const API_BASE = (typeof window !== 'undefined' && window.location.hostname === 'localhost') ? '' : 'https://api.glowytics.com';

const fmtInt = (n) => {
  const v = Number(n || 0);
  if (!Number.isFinite(v)) return '-';
  return v.toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
};
const fmtMoney = (n) => `₺${fmtInt(n)}`;
const fmtPct = (n) => {
  if (n === null || n === undefined || !Number.isFinite(Number(n))) return '-';
  return `%${Number(n).toFixed(1)}`;
};

function gpmColor(gpm) {
  if (gpm === null || gpm === undefined) return '';
  if (gpm < 20) return 'text-red-600';
  if (gpm < 35) return 'text-orange-500';
  if (gpm < 50) return 'text-green-600';
  return 'text-emerald-700';
}

function coverageLabel(months) {
  if (months === null || months === undefined || !Number.isFinite(Number(months))) return 'No sales';
  if (months <= 2) return 'Low';
  if (months <= 4) return 'Medium';
  if (months <= 6) return 'Healthy';
  if (months <= 8) return 'High';
  return 'Overstock';
}

function coverageBadgeClass(months) {
  if (months === null || months === undefined || !Number.isFinite(Number(months))) return 'bg-gray-100 text-gray-500';
  if (months <= 2) return 'bg-red-100 text-red-700';
  if (months <= 4) return 'bg-orange-100 text-orange-700';
  if (months <= 6) return 'bg-green-100 text-green-700';
  if (months <= 8) return 'bg-blue-100 text-blue-700';
  return 'bg-purple-100 text-purple-700';
}

const CONTRACT_GP = {
  'Nike': 42.20, 'New Balance': 47.09, 'WUNDER': 66.67, 'Jordan': 42.20,
  'adidas': 43.50, 'asics': 50.00, 'Converse': 50.00, 'On': 41.18,
  'Between Sides': 50.00, 'Puma': 46.81, 'Y-3': 62.96, 'Dickies': 50.00,
  'VEJA': 50.00, 'Carhartt WIP': 50.00, 'Vans': 50.00, 'Tommy Jeans': 47.37,
  'Salomon': 44.44, 'New Era': 44.44, 'Fred Perry': 52.15,
  'thisisneverthat': 50.00, 'The North Face': 50.00, 'ROA': 50.00,
  'Gramicci': 50.00, 'BIRKENSTOCK': 41.18, 'Timberland': 50.00,
  'SUNSET APOLLON': 60.00, 'Arte Antwerp': 50.00, 'Aries': 50.00,
  'Pop Trading Company': 50.00, 'and wander': 50.00, 'EPHEMERAL GOODS': 69.23,
  'Butter Goods': 50.00, 'Sebago': 47.37, 'Calvin Klein': 47.37,
  'Jason Markk': 50.00, 'HOKA': 41.18, 'UMBRO': 50.00, 'A.P.C.': 50.00,
  'Parel': 50.00, 'Stockholm Surfboard Club': 50.00, 'HÄRMAN x QUILTED': 23.08,
  'Polaroid': 16.67, 'Crocs': 45.95, '19-69': 35.48, 'Oakley': 50.00,
  'SANYA': 70.15, 'Perplex': 57.45, 'District Vision': 50.00,
  'Merely Made': 50.00, 'EPICENE': 56.52, 'No Problemo': 50.00,
  'IDEA': 50.00, 'Dime': 50.00,
};

function lookupContractGP(brand) {
  if (!brand) return null;
  const exact = CONTRACT_GP[brand];
  if (exact !== undefined) return exact;
  const lower = brand.toLowerCase();
  for (const [k, v] of Object.entries(CONTRACT_GP)) {
    if (k.toLowerCase() === lower) return v;
    if (lower.startsWith(k.toLowerCase()) || k.toLowerCase().startsWith(lower)) return v;
  }
  return null;
}

export default function IkasMonthlyFetch() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [sortKey, setSortKey] = useState('netSales');
  const [sortDir, setSortDir] = useState('desc');
  const [channelFilter, setChannelFilter] = useState('');
  const [availableChannels, setAvailableChannels] = useState([]);
  const [dbBrandMap, setDbBrandMap] = useState({});

  const fetchData = useCallback(async () => {
    const brandId = localStorage.getItem('currentBrandId');
    if (!brandId) { setError('No brand selected'); return; }
    setLoading(true); setError(''); setData(null);
    try {
      const params = new URLSearchParams();
      if (channelFilter) params.set('channel', channelFilter);
      const hdrs = { credentials: 'include', headers: { 'X-Brand-ID': brandId } };

      const [salesRes, stockRes] = await Promise.all([
        fetch(`${API_BASE}/api/ikas/live/monthly-fetch?${params}`, hdrs),
        fetch(`${API_BASE}/api/ikas/stock/coverage?months=6`, hdrs),
      ]);

      const json = await salesRes.json();
      if (!salesRes.ok || !json.ok) throw new Error(json.error || 'Fetch failed');

      const bMap = {};
      try {
        const stockJson = await stockRes.json();
        if (stockJson?.brands) {
          for (const b of stockJson.brands) {
            bMap[b.brand] = {
              stockQty: Number(b.stockQty || 0),
              totalSold6m: Number(b.totalSold6m || 0),
              avgMonthlySales: Number(b.avgMonthlySales || 0),
              coverageMonths: b.coverageMonths === null || b.coverageMonths === undefined ? null : Number(b.coverageMonths),
            };
          }
        }
      } catch (_) { /* stock fetch optional */ }

      setDbBrandMap(bMap);
      setData(json);
      if (json.availableChannels?.length) setAvailableChannels(json.availableChannels);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [channelFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('desc'); }
  };

  const SortIcon = ({ col }) => {
    if (sortKey !== col) return <ChevronDown className="w-3 h-3 opacity-30 inline" />;
    return sortDir === 'asc' ? <ChevronUp className="w-3 h-3 inline text-blue-600" /> : <ChevronDown className="w-3 h-3 inline text-blue-600" />;
  };

  const EXCLUDED_BRANDS = [
    'Champion', 'Dime', 'TASCHEN', 'Phaidon', 'Abrams',
    'HÄRMAN COFFEE', 'District Vision', 'WUNDER x shopi go', 'EPICENE'
  ];
  const isExcluded = (brand) => EXCLUDED_BRANDS.some(
    ex => ex.toLowerCase() === (brand || '').toLowerCase()
  );

  const sortedRows = useMemo(() => {
    if (!data?.brands) return [];
    let rows = data.brands.filter(r => !isExcluded(r.brand)).map(r => {
      const db = dbBrandMap[r.brand] || {};
      const fallbackAvgMonthlySales = Number(r.items || 0) / 6;
      const avgMonthlySales = db.avgMonthlySales !== undefined ? Number(db.avgMonthlySales || 0) : fallbackAvgMonthlySales;
      const coverageMonths = db.coverageMonths !== undefined
        ? db.coverageMonths
        : (avgMonthlySales > 0 ? Number(db.stockQty || 0) / avgMonthlySales : null);
      return {
        ...r,
        stockQty: db.stockQty || 0,
        totalSold180dQty: db.totalSold6m !== undefined ? Number(db.totalSold6m || 0) : Number(r.items || 0),
        avgMonthlySales: Math.round(avgMonthlySales),
        coverageMonths: coverageMonths === null ? null : Math.round(coverageMonths * 10) / 10,
        coverageStatus: coverageLabel(coverageMonths),
        netSalesExclTax: Math.round(Number(r.netSales || 0) / 1.10),
        cogs: Math.round(Number(r.netCogs || 0)),
        grossProfit: Math.round(Number(r.grossProfit || 0)),
        gpm: r.gpm,
        contractGP: lookupContractGP(r.brand),
      };
    });
    const dir = sortDir === 'asc' ? 1 : -1;
    rows.sort((a, b) => {
      const av = a[sortKey], bv = b[sortKey];
      if (typeof av === 'string') return av.localeCompare(bv) * dir;
      const na = av === null || av === undefined ? -Infinity : Number(av);
      const nb = bv === null || bv === undefined ? -Infinity : Number(bv);
      return (na - nb) * dir;
    });
    return rows;
  }, [data, dbBrandMap, sortKey, sortDir]);

  const exportCSV = () => {
    if (!sortedRows.length) return;
    const cols = Object.keys(sortedRows[0]);
    const lines = [cols.join(',')];
    for (const r of sortedRows) {
      lines.push(cols.map(c => {
        const v = r[c];
        if (typeof v === 'string' && (v.includes(',') || v.includes('"'))) return `"${v.replace(/"/g, '""')}"`;
        return v ?? '';
      }).join(','));
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
    a.download = 'ikas-brands-last180d.csv'; a.click();
  };

  const colDefs = [
    { key: 'brand', label: 'Brand', type: 'text' },
    { key: 'stockQty', label: 'Stock Qty', type: 'num' },
    { key: 'totalSold180dQty', label: '180d Sales (qty)', type: 'num' },
    { key: 'avgMonthlySales', label: 'Avg Monthly Sales', type: 'num' },
    { key: 'coverageMonths', label: 'Coverage (mo)', type: 'coverage' },
    { key: 'coverageStatus', label: 'Status', type: 'status' },
    { key: 'netSalesExclTax', label: 'Net Sales (KDV hariç)', type: 'money' },
    { key: 'cogs', label: 'COGS', type: 'money' },
    { key: 'grossProfit', label: 'Gross Profit', type: 'money' },
    { key: 'gpm', label: 'GPM %', type: 'pct' },
    { key: 'contractGP', label: 'Contract GP%', type: 'pct' },
  ];

  const totals = useMemo(() => {
    if (!sortedRows.length) return {};
    const t = {};
    for (const col of colDefs) {
      if (col.type === 'money' || col.type === 'num') {
        t[col.key] = sortedRows.reduce((a, r) => a + Number(r[col.key] || 0), 0);
      }
    }
    t.gpm = t.netSalesExclTax > 0 ? Math.round((t.grossProfit / t.netSalesExclTax) * 1000) / 10 : null;
    return t;
  }, [sortedRows]);

  const kpiSummary = useMemo(() => {
    if (!sortedRows.length) {
      return {
        avgCoverage: null,
        gpmAboveAvg: 0,
        gpmBelowAvg: 0,
        gpmEqualAvg: 0,
        gpmNoData: 0,
        coverage: { low: 0, medium: 0, healthy: 0, high: 0, overstock: 0, noSales: 0 },
      };
    }

    const avgGpm = Number(totals?.gpm);
    let gpmAboveAvg = 0;
    let gpmBelowAvg = 0;
    let gpmEqualAvg = 0;
    let gpmNoData = 0;

    let covCount = 0;
    let covTotal = 0;
    const coverage = { low: 0, medium: 0, healthy: 0, high: 0, overstock: 0, noSales: 0 };

    for (const row of sortedRows) {
      const rowGpm = Number(row.gpm);
      if (Number.isFinite(rowGpm) && Number.isFinite(avgGpm)) {
        if (rowGpm > avgGpm) gpmAboveAvg += 1;
        else if (rowGpm < avgGpm) gpmBelowAvg += 1;
        else gpmEqualAvg += 1;
      } else {
        gpmNoData += 1;
      }

      const cm = row.coverageMonths;
      if (cm === null || cm === undefined || !Number.isFinite(Number(cm))) {
        coverage.noSales += 1;
      } else {
        const v = Number(cm);
        covCount += 1;
        covTotal += v;
        if (v <= 2) coverage.low += 1;
        else if (v <= 4) coverage.medium += 1;
        else if (v <= 6) coverage.healthy += 1;
        else if (v <= 8) coverage.high += 1;
        else coverage.overstock += 1;
      }
    }

    return {
      avgCoverage: totals.avgMonthlySales > 0 ? Math.round((totals.stockQty / totals.avgMonthlySales) * 10) / 10 : null,
      gpmAboveAvg,
      gpmBelowAvg,
      gpmEqualAvg,
      gpmNoData,
      coverage,
    };
  }, [sortedRows, totals]);

  const renderCell = (col, v, row) => {
    if (col.type === 'money') return fmtMoney(v);
    if (col.type === 'num') return fmtInt(v);
    if (col.type === 'pct') {
      if (col.key === 'gpm') {
        const avgGpm = Number(totals?.gpm);
        const rowGpm = Number(v);
        let cls = '';
        if (Number.isFinite(rowGpm) && Number.isFinite(avgGpm)) {
          if (rowGpm > avgGpm) cls = 'text-green-600';
          else if (rowGpm < avgGpm) cls = 'text-red-600';
          else cls = 'text-gray-700';
        } else {
          cls = gpmColor(v);
        }
        return <span className={cls}>{fmtPct(v)}</span>;
      }
      return <span className={gpmColor(v)}>{fmtPct(v)}</span>;
    }
    if (col.type === 'coverage') return v === null || v === undefined ? '-' : Number(v).toFixed(1);
    if (col.type === 'status') {
      const cm = row?.coverageMonths;
      const maxCoverage = 24;
      const pct = cm !== null && cm !== undefined && Number.isFinite(Number(cm)) ? Math.min(Number(cm) / maxCoverage * 100, 100) : 0;
      const barColor = cm === null || cm === undefined || !Number.isFinite(Number(cm)) ? 'bg-gray-200'
        : cm <= 2 ? 'bg-red-400'
        : cm <= 4 ? 'bg-orange-400'
        : cm <= 6 ? 'bg-green-400'
        : cm <= 8 ? 'bg-blue-400' : 'bg-purple-400';
      return (
        <div className="flex items-center gap-2 min-w-[140px]">
          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap ${coverageBadgeClass(cm)}`}>
            {v || '-'}
          </span>
          <div className="flex-1 bg-gray-100 rounded-full h-2 min-w-[60px]">
            <div className={`h-2 rounded-full transition-all duration-300 ${barColor}`} style={{ width: `${pct}%` }} />
          </div>
        </div>
      );
    }
    return v || '-';
  };

  return (
    <div className="p-6 max-w-[1400px] mx-auto space-y-6">
      <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-4">
        <div className="flex flex-wrap justify-between items-center gap-3">
          <div className="flex items-center gap-3">
            <img src="/ikaslogoicon.png" alt="Ikas" className="w-10 h-10 object-contain rounded-lg" />
            <div>
              <h1 className="text-xl font-bold text-gray-900">ikas Live Data — Brand Breakdown</h1>
              <p className="text-xs text-gray-500">Sales & COGS from Ikas API · Stock from DB · Contract GP% static</p>
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="border rounded-lg px-3 py-2 text-sm bg-gray-50 text-gray-700 font-medium">
            Last 180 days (today excluded)
          </div>
          {availableChannels.length > 0 && (
            <select className="border rounded-lg px-2 py-2 text-sm bg-white" value={channelFilter} onChange={e => setChannelFilter(e.target.value)}>
              <option value="">All Channels</option>
              {availableChannels.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          )}
          <button
            className="flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-lg text-sm font-medium hover:bg-gray-800 disabled:opacity-50 transition-colors"
            onClick={fetchData} disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            {loading ? 'Fetching...' : 'Fetch Data'}
          </button>
        </div>

        {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm">{error}</div>}

        {loading && !data && (
          <div className="bg-white rounded-2xl shadow-xl border p-10 flex flex-col items-center justify-center gap-3">
            <RefreshCw className="w-8 h-8 animate-spin text-gray-500" />
            <p className="text-sm text-gray-600 font-medium">Loading last 180 days data...</p>
          </div>
        )}

        {data && !loading && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
              <div className="bg-white border rounded-xl p-4 text-center">
                <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide">180D Sales (qty)</div>
                <div className="text-2xl font-extrabold mt-1 text-gray-900">{fmtInt(totals.totalSold180dQty)}</div>
                <div className="text-xs text-gray-400 mt-1">units</div>
              </div>
              <div className="bg-white border rounded-xl p-4 text-center">
                <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Avg Monthly Sales</div>
                <div className="text-2xl font-extrabold mt-1 text-gray-900">{fmtInt(totals.avgMonthlySales)}</div>
                <div className="text-xs text-gray-400 mt-1">units / month</div>
              </div>
              <div className="bg-white border rounded-xl p-4 text-center">
                <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Gross Profit Margin (GPM)</div>
                <div className={`text-2xl font-extrabold mt-1 ${gpmColor(data.totals?.gpm)}`}>{fmtPct(data.totals?.gpm)}</div>
                <div className="text-xs text-gray-400 mt-1">excl. VAT · last 180 days</div>
              </div>
              <div className="bg-white border rounded-xl p-4 text-center">
                <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Avg Coverage</div>
                <div className="text-2xl font-extrabold mt-1 text-gray-900">{kpiSummary.avgCoverage !== null ? `${kpiSummary.avgCoverage} mo` : '—'}</div>
                <div className="text-xs text-gray-400 mt-1">{coverageLabel(kpiSummary.avgCoverage ? Number(kpiSummary.avgCoverage) : null)}</div>
              </div>
              <div className="bg-white border rounded-xl p-4 text-center">
                <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide">GPM vs Avg</div>
                <div className="mt-1">
                  <span className="text-green-600 font-bold text-lg">{kpiSummary.gpmAboveAvg}</span>
                  <span className="text-xs text-gray-400"> above · </span>
                  <span className="text-red-600 font-bold text-lg">{kpiSummary.gpmBelowAvg}</span>
                  <span className="text-xs text-gray-400"> below</span>
                </div>
                <div className="text-xs text-gray-400 mt-1">{kpiSummary.gpmEqualAvg} equal · {kpiSummary.gpmNoData} no data</div>
              </div>
              <div className="bg-white border rounded-xl p-4 text-center">
                <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Date Range (180d)</div>
                <div className="text-sm font-extrabold mt-2 text-gray-900">{data.dateRange?.start}</div>
                <div className="text-xs text-gray-400">→ {data.dateRange?.end}</div>
              </div>
            </div>

            <div className="grid grid-cols-6 gap-2 text-center text-xs">
              <div className="bg-red-50 border border-red-100 rounded-lg p-2">
                <div className="font-bold text-red-600 text-lg">{kpiSummary.coverage.low}</div>
                <div className="text-red-500">Low (≤2 mo)</div>
              </div>
              <div className="bg-orange-50 border border-orange-100 rounded-lg p-2">
                <div className="font-bold text-orange-600 text-lg">{kpiSummary.coverage.medium}</div>
                <div className="text-orange-500">Medium (2-4 mo)</div>
              </div>
              <div className="bg-green-50 border border-green-100 rounded-lg p-2">
                <div className="font-bold text-green-600 text-lg">{kpiSummary.coverage.healthy}</div>
                <div className="text-green-500">Healthy (4-6 mo)</div>
              </div>
              <div className="bg-blue-50 border border-blue-100 rounded-lg p-2">
                <div className="font-bold text-blue-600 text-lg">{kpiSummary.coverage.high}</div>
                <div className="text-blue-500">High (6-8 mo)</div>
              </div>
              <div className="bg-purple-50 border border-purple-100 rounded-lg p-2">
                <div className="font-bold text-purple-600 text-lg">{kpiSummary.coverage.overstock}</div>
                <div className="text-purple-500">Overstock (8+ mo)</div>
              </div>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-2">
                <div className="font-bold text-gray-600 text-lg">{kpiSummary.coverage.noSales}</div>
                <div className="text-gray-500">No sales data</div>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-xl border p-4">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <h3 className="text-sm font-semibold text-gray-700">Brands ({sortedRows.length})</h3>
                <button className="flex items-center gap-1.5 px-3 py-1.5 text-sm border rounded-lg hover:bg-gray-50" onClick={exportCSV}>
                  <Download className="w-4 h-4" /> CSV
                </button>
              </div>

              <div className="overflow-x-auto">
                <div className="max-h-[600px] overflow-y-auto">
                  <table className="min-w-full text-sm">
                    <thead className="sticky top-0 bg-white z-10">
                      <tr className="border-b">
                        <th className="py-2 pr-3 text-left text-xs text-gray-500">#</th>
                        {colDefs.map(col => (
                          <th key={col.key} className="py-2 pr-4 text-left text-xs text-gray-500 cursor-pointer select-none hover:text-gray-900" onClick={() => toggleSort(col.key)}>
                            {col.label} <SortIcon col={col.key} />
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {sortedRows.length === 0 ? (
                        <tr><td className="py-6 text-center text-gray-400" colSpan={99}>No data</td></tr>
                      ) : sortedRows.map((row, idx) => (
                        <tr key={idx} className="border-b hover:bg-gray-50">
                          <td className="py-2 pr-3 text-xs text-gray-400">{idx + 1}</td>
                          {colDefs.map(col => (
                            <td key={col.key} className="py-2 pr-4 whitespace-nowrap">{renderCell(col, row[col.key], row)}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                    {sortedRows.length > 0 && (
                      <tfoot className="bg-gray-50 font-semibold sticky bottom-0">
                        <tr className="border-t">
                          <td className="py-2 pr-3"></td>
                          {colDefs.map((col, ci) => {
                            if (ci === 0) return <td key={col.key} className="py-2 pr-4">Total</td>;
                            if (col.key === 'gpm') return <td key={col.key} className={`py-2 pr-4 ${gpmColor(totals.gpm)}`}>{fmtPct(totals.gpm)}</td>;
                            if (col.key === 'contractGP') return <td key={col.key} className="py-2 pr-4"></td>;
                            if (col.type === 'money') return <td key={col.key} className="py-2 pr-4">{fmtMoney(totals[col.key])}</td>;
                            if (col.type === 'num') return <td key={col.key} className="py-2 pr-4">{fmtInt(totals[col.key])}</td>;
                            return <td key={col.key} className="py-2 pr-4"></td>;
                          })}
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
